/* eslint-disable max-len */
import { isResponseError, Utilities } from './../../../helpers/utils';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { RootState } from '../..';
import {
    selectAppConfig,
    selectReplyToId,
    selectStubUrl,
} from '../../selectors/app';
import { selectUser, selectUserId } from '../../selectors/auth';
import {
    OperationId,
    setFilesToUpload,
    setProgressSize,
    showNotification,
    startFetch,
    stopFetch,
} from './actionCreators';
import { appConstants, AppErrorCode, NotificationStatus } from './actionTypes';
import {
    ApiResponse,
    ItemType,
    OCSOkResponseBody,
    Share,
    SharePermissions,
    ShareType,
    nextcloudApi,
    webdavApi,
} from 'nextcloud-api';
import { appActions, createShareFolders } from './actions';
import * as path from 'path';
import {
    ContentType,
    CreateFolderData,
    FileItem,
    RenameFileData,
    ServerFile,
} from '../../../model';
import {
    getRenamedFilePath,
    getStartingPathParts,
} from '../../../helpers/fileExplorer';
import { selectTranslate } from '../../selectors/ui';
import { batch } from 'react-redux';
import {
    DndEmailShareDialogResult,
    DndPublicShareDialogResult,
    DndShareWithCirclesDialogResult,
    EmailShareDialogResult,
    SharedFileInfo,
    ShareFileDialogResult,
    submitDialogResult,
} from '../../../services/dialogResults';
import { setUserIdsResponse } from '../conversation/actionCreators';
import { AppErrorCode as NextcloudAppErrorCode } from 'nextcloud-api';
import {
    selectQueryToUserIdsResponse,
    selectTeamsContext,
} from '../../selectors/conversation';
import { ErrOption } from '../../../helpers/model/errOption';
import * as microsoftTeams from '@microsoft/teams-js';
import { mapPlainFilesToFileItems } from '../../../helpers/file';
import { uploadFilesInParallel } from '../../../services/file';
import { openFileInModalWindow } from '../modalWindow/thunks';
import { setFileId } from '../modalWindow/actionCreators';
import { selectFileId } from '../../selectors/modalWindow';
import {
    selectDefaultFolderPath,
    selectDefaultFolderLoaded,
} from '../../selectors/defaultFolder';
import { Circle } from '../../../services/microsoft/msGraphApi';
import { RefBox } from '../../../helpers/refBox';
import { RetryOptions } from 'nextcloud-api/dist/models/webdav/retry-upload-model';
import { WebdavApiErrorSubCode } from 'nextcloud-api/dist/models/error-model';
import { MEGABYTE } from '../../../helpers/constants';
import { UploadChunkedFileParams } from 'nextcloud-api/dist/models/webdav/chunked-file-model';
import { logger } from '../../../helpers/logger';

export const chunkedUploadAbortControllerRefBox = new RefBox<AbortController>(
    new AbortController(),
);

const MAX_UPLOAD_RETRIES = 3;
const BASE_DELAY_MS = 1000;

const ALLOWED_MOBILE_MIMETYPES: ContentType[] & string[] = [
    ContentType.ImageJpeg,
    ContentType.ImagePng,
    ContentType.VideoMp4,
    ContentType.ApplicationJson,
    ContentType.TextMarkdown,
    ContentType.TextPlain,
] as const;

const ALLOWED_MODAL_FILE_TYPES: ContentType[] & string[] = [
    ContentType.ImagePng,
    ContentType.ImageJpeg,
    ContentType.VideoMp4,
    ContentType.ApplicationJson,
    ContentType.TextPlain,
    ContentType.TextMarkdown,
    ContentType.ApplicationPdf,
] as const;

const OFFICE_PROTOCOL_MAP: Record<string, string> = {
    '.doc': 'ms-word:ofe|u|',
    '.docx': 'ms-word:ofe|u|',
    '.ppt': 'ms-powerpoint:ofe|u|',
    '.pptx': 'ms-powerpoint:ofe|u|',
    '.xls': 'ms-excel:ofe|u|',
    '.xlsx': 'ms-excel:ofe|u|',
};

const LOCALLY_OPENABLE_EXTENSIONS = Object.keys(OFFICE_PROTOCOL_MAP);

/** Checks whether a file has a supported Office extension to open locally. */
export const isOpenableLocally = (filePath: string): boolean => {
    const ext = path.extname(filePath).toLowerCase();
    return LOCALLY_OPENABLE_EXTENSIONS.includes(ext);
};

export const copyFileShareLinkToClipboardThunk = (
    fileId: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return;
        }

        const link = `${user.server}/index.php/f/${fileId}`;

        try {
            await navigator.permissions.query({
                // eslint-disable-next-line max-len
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                name: 'clipboard-write',
            });
        } catch (err) {
            logger.error(
                `clipboard-write permission query error: ${
                    err instanceof Error ? err.message : 'unknown'
                }`,
                'Share',
            );
            if (!(err instanceof TypeError)) {
                dispatch(
                    showNotification({
                        errorCode: AppErrorCode.CustomError,
                        message: translate('allow_clipboard_access'),
                        status: NotificationStatus.Error,
                    }),
                );
                return;
            }
        }

        try {
            await navigator.clipboard.writeText(link);
        } catch (err) {
            logger.error(
                `clipboard writeText error: ${
                    err instanceof Error ? err.message : 'unknown'
                }`,
                'Share',
            );
            dispatch(
                showNotification({
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        dispatch(
            showNotification({
                message: translate('link_copied_to_clipboard'),
                status: NotificationStatus.Success,
            }),
        );
    };
};

export const renameFileThunk = ({
    initialPath,
    newFilename,
}: RenameFileData): ThunkAction<void, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return;
        }

        dispatch(
            startFetch({
                id: OperationId.RenameFile,
                message: translate('renaming_item'),
            }),
        );

        const newPath = getRenamedFilePath(initialPath, newFilename);
        const fileDir = path.parse(newPath).dir;
        const res = await webdavApi.moveFile(
            stubUrl,
            user,
            userId,
            initialPath,
            newPath,
        );
        if (!res.success) {
            await dispatch(appActions.getServerFiles(fileDir));
            dispatch(
                stopFetch({
                    operationId: OperationId.RenameFile,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        await dispatch(appActions.getServerFiles(fileDir));

        dispatch(
            stopFetch({
                operationId: OperationId.RenameFile,
            }),
        );
    };
};

export const deleteFileByPathThunk = (
    filePath: string,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return;
        }

        dispatch(
            startFetch({
                id: OperationId.DeleteFile,
                message: translate('deleting_item'),
            }),
        );

        const fileDir = path.parse(filePath).dir;

        const res = await webdavApi.deleteFileByPath(
            stubUrl,
            user,
            userId,
            filePath,
        );
        if (!res.success) {
            await dispatch(appActions.getServerFiles(fileDir));
            dispatch(
                stopFetch({
                    operationId: OperationId.DeleteFile,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        await dispatch(appActions.getServerFiles(fileDir));

        batch(() => {
            dispatch(
                stopFetch({
                    operationId: OperationId.DeleteFile,
                }),
            );
            dispatch(
                showNotification({
                    message: translate('item_deleted'),
                    status: NotificationStatus.Success,
                }),
            );
        });
    };
};

export const openDownloadLinkThunk = (
    filePath: string,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        if (!user || !userId) {
            return;
        }

        const url = `${user.server}/remote.php/dav/files/${userId}/${filePath}`;
        window.open(url, '_blank');
    };
};

export const openFileThunk = (
    file: ServerFile,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    return function thunk(dispatch, getState): void {
        const state = getState();
        const previousFileId = selectFileId(state);
        const appConfig = selectAppConfig(state);

        dispatch(setFileId(file.fileId));

        const isFileTooLarge =
            file.contentlength / MEGABYTE >= appConfig.maxModalFileSizeMb;
        if (
            isFileTooLarge ||
            !ALLOWED_MODAL_FILE_TYPES.includes(file.contenttype)
        ) {
            dispatch(openFileInNextcloudThunk(file.fileId));
            return;
        }

        const userAgent = navigator.userAgent.toLowerCase();
        const isMobile = /iphone|ipod|ipad|android/.test(userAgent);

        if (isMobile && !ALLOWED_MOBILE_MIMETYPES.includes(file.contenttype)) {
            dispatch(openFileInNextcloudThunk(file.fileId));
            return;
        }

        dispatch(openFileInModalWindow(file, previousFileId));
    };
};

export const openFileInNextcloudThunk = (
    fileId: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    return function thunk(dispatch, getState): void {
        const user = selectUser(getState());
        if (!user) {
            return;
        }

        const url = `${user.server}/index.php/f/${fileId}`;
        window.open(url, '_blank');
    };
};

/**
 * Opens a Nextcloud-hosted Office file in the user's local MS Office application.
 * This is achieved by constructing a Webdav URL from the user's server and file path,
 * then using the appropriate protocol handler to launch the native application.
 *
 * Only supports extensions defined in {@link OFFICE_PROTOCOL_MAP}.
 *
 * @param filePath - the file path of the file being opened
 */
export const openFileLocallyThunk = (
    filePath: string,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    return function thunk(_dispatch, getState): void {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        if (!user || !userId) return;

        const ext = path.extname(filePath).toLowerCase();
        const protocol = OFFICE_PROTOCOL_MAP[ext];
        if (!protocol) return;

        const encodedPath = filePath
            .split('/')
            .map(encodeURIComponent)
            .join('/');
        const webdavUrl = `${user.server}/remote.php/dav/files/${userId}/${encodedPath}`;
        window.open(`${protocol}${webdavUrl}`, '_blank');
    };
};

/**
 * Opens a Nextcloud-hosted Office in the Nextcloud desktop client.
 *
 * @param filePath - the file path of the file being opened
 */
export const openFileInDesktopClientThunk = (
    filePath: string,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !stubUrl) {
            return;
        }

        if (filePath.includes('..') || filePath.includes('\0')) {
            logger.error(`Suspicious file path rejected: ${filePath}`, 'Files');
            return;
        }

        const res = await nextcloudApi.requestOpenLocalEditorToken({
            requestConfig: { stubUrl, user },
            filePath: `/${filePath}`,
        });

        if (!res.success) {
            dispatch(
                showNotification({
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        const token = res.result.ocs.data.token;
        const serverHost = new URL(user.server).host;

        window.open(`nc://open/${token}@${serverHost}/${filePath}`, '_blank');
    };
};

export const getServerFilesWithLoader = (
    parentFolderPath: string,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, _getState): Promise<void> {
        dispatch(
            startFetch({
                id: OperationId.LoadUserConfiguration,
                message: 'load_serverfiles',
            }),
        );

        await dispatch(appActions.getServerFiles(parentFolderPath));

        dispatch(
            stopFetch({
                operationId: OperationId.LoadUserConfiguration,
            }),
        );
    };
};

export const getInitialServerFilesThunk = (): ThunkAction<
    void,
    RootState,
    unknown,
    AnyAction
> => {
    return function thunk(dispatch, getState) {
        const defaultFolderPath = selectDefaultFolderPath(getState());
        const defaultFolderLoaded = selectDefaultFolderLoaded(getState());

        if (defaultFolderLoaded && defaultFolderPath) {
            dispatch(getServerFilesWithLoader(defaultFolderPath));
            return;
        }

        const startingPathParts = getStartingPathParts();
        if (!startingPathParts) {
            dispatch(getServerFilesWithLoader(''));
            return;
        }
        dispatch(getServerFilesWithLoader(startingPathParts.at(-1).path));
    };
};

type UploadFileData = {
    folder: string;
    fileBase: string;
    content: string | ArrayBuffer;
};

export enum UploadFileThunkError {
    InvalidState = 'invalid_state',
    FileExistsCheckFailed = 'file_exists_check_failed',
    FileExists = 'file_exists',
    FileUploadFailed = 'file_upload_failed',
}

export const uploadFileThunk = ({
    folder,
    fileBase,
    content,
}: UploadFileData): ThunkAction<
    Promise<ApiResponse | ErrOption<UploadFileThunkError>>,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState) {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return new ErrOption(
                'Invalid state',
                UploadFileThunkError.InvalidState,
            );
        }

        dispatch(
            startFetch({
                id: OperationId.UploadFile,
                message: translate('upload_selected'),
            }),
        );

        const uploadPath = `${folder}/${fileBase}`;
        const fileExistsRes = await webdavApi.exists(
            stubUrl,
            user,
            userId,
            uploadPath,
        );
        if (!fileExistsRes.success) {
            await dispatch(appActions.getServerFiles(folder));
            dispatch(
                stopFetch({
                    operationId: OperationId.UploadFile,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                }),
            );
            return new ErrOption(
                fileExistsRes.result.message,
                UploadFileThunkError.FileExistsCheckFailed,
            );
        }
        if (fileExistsRes.result) {
            await dispatch(appActions.getServerFiles(folder));
            dispatch(
                stopFetch({
                    operationId: OperationId.UploadFile,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('file_already_exists'),
                    status: NotificationStatus.Error,
                }),
            );
            return new ErrOption(
                `File ${uploadPath} already exists`,
                UploadFileThunkError.FileExists,
            );
        }

        const uploadRes = await webdavApi.copyOrCreateFile(
            stubUrl,
            user,
            userId,
            folder,
            uploadPath,
            content,
            () => {},
        );
        if (!uploadRes.success) {
            await dispatch(appActions.getServerFiles(folder));
            dispatch(
                stopFetch({
                    operationId: OperationId.UploadFile,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                }),
            );
            return new ErrOption(
                uploadRes.result.message,
                UploadFileThunkError.FileUploadFailed,
            );
        }

        await dispatch(appActions.getServerFiles(folder));

        batch(() => {
            dispatch(
                stopFetch({
                    operationId: OperationId.UploadFile,
                }),
            );
            dispatch(
                showNotification({
                    message: translate('upload_finished_header'),
                    status: NotificationStatus.Success,
                }),
            );
        });

        return uploadRes.result;
    };
};

type UploadLargeFileData = Omit<UploadFileData, 'content'> & {
    file: File;
};

export const uploadLargeFileThunk = ({
    folder,
    file,
    fileBase,
}: UploadLargeFileData): ThunkAction<
    Promise<
        ApiResponse | ErrOption<UploadFileThunkError | WebdavApiErrorSubCode>
    >,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState) {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return new ErrOption(
                'Invalid state',
                UploadFileThunkError.InvalidState,
            );
        }

        const uploadPath = `${folder}/${fileBase}`;

        const fileExistsRes = await webdavApi.exists(
            stubUrl,
            user,
            userId,
            uploadPath,
        );

        if (!fileExistsRes.success) {
            await dispatch(appActions.getServerFiles(folder));

            return new ErrOption(
                fileExistsRes.result.message,
                UploadFileThunkError.FileExistsCheckFailed,
            );
        }
        if (fileExistsRes.result) {
            await dispatch(appActions.getServerFiles(folder));

            return new ErrOption(
                `File ${uploadPath} already exists`,
                UploadFileThunkError.FileExists,
            );
        }

        const abortController = new AbortController();
        chunkedUploadAbortControllerRefBox.setValue(abortController);

        const retryOptions: RetryOptions = {
            maxRetries: MAX_UPLOAD_RETRIES,
            baseDelayMs: BASE_DELAY_MS,
            calculateDelay: (retryAttempt, baseDelayMs) => {
                return baseDelayMs * 2 ** (retryAttempt - 1);
            },
        };

        const chunkedFileParams: UploadChunkedFileParams = {
            destination: uploadPath,
            content: file.stream(),
            fileSize: file.size,
            signal: abortController.signal,
            chunkSizeBytes: state.appReducer.appConfig.chunkSizeMb * MEGABYTE,
            onUploadProgress: (progress) => {
                dispatch(setProgressSize(progress));
            },
            retryOptions,
        };

        const uploadRes = await webdavApi.uploadChunkedFile({
            requestConfig: {
                stubUrl,
                user,
                userId,
            },
            ...chunkedFileParams,
        });

        if (!uploadRes.success) {
            if (
                uploadRes.result.errorSubCode ===
                WebdavApiErrorSubCode.NetworkError
            ) {
                return new ErrOption(
                    uploadRes.result.message,
                    WebdavApiErrorSubCode.NetworkError,
                );
            }

            return new ErrOption(
                uploadRes.result.message,
                UploadFileThunkError.FileUploadFailed,
            );
        }

        await dispatch(appActions.getServerFiles(folder));

        dispatch(
            showNotification({
                message: translate('upload_finished_header'),
                status: NotificationStatus.Success,
            }),
        );

        return uploadRes.result;
    };
};

export enum UploadShareFileThunkError {
    InvalidState = 'invalid_state',
    FolderCreationFailed = 'folder_creation_failed',
    FileExistsCheckFailed = 'file_exists_check_failed',
    FileExists = 'file_exists',
    FileUploadFailed = 'file_upload_failed',
    UploadedFileNotFound = 'uploaded_file_not_found',
}

type UploadShareFileData = UploadFileData & { allowOverwrite: boolean };

export const uploadShareFileThunk = ({
    folder,
    fileBase,
    content,
    allowOverwrite,
}: UploadShareFileData): ThunkAction<
    Promise<ServerFile | ErrOption<UploadShareFileThunkError>>,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState) {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return new ErrOption(
                'Invalid state',
                UploadShareFileThunkError.InvalidState,
            );
        }

        dispatch(
            startFetch({
                id: OperationId.UploadFile,
                message: translate('upload_selected'),
            }),
        );

        const uploadPath = `${folder}/${fileBase}`;

        const isFolderCreated = await createShareFolders(folder);

        if (!isFolderCreated) {
            dispatch(
                stopFetch({
                    operationId: OperationId.UploadFile,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                }),
            );

            return new ErrOption(
                `Could not create folder for ${uploadPath}`,
                UploadShareFileThunkError.FolderCreationFailed,
            );
        }

        if (!allowOverwrite) {
            const fileExistsRes = await webdavApi.exists(
                stubUrl,
                user,
                userId,
                uploadPath,
            );
            if (!fileExistsRes.success) {
                dispatch(
                    stopFetch({
                        operationId: OperationId.UploadFile,
                        errorCode: AppErrorCode.CustomError,
                        message: translate('upload_selected_error'),
                        status: NotificationStatus.Error,
                    }),
                );
                return new ErrOption(
                    fileExistsRes.result.message,
                    UploadShareFileThunkError.FileExistsCheckFailed,
                );
            }
            if (fileExistsRes.result) {
                dispatch(stopFetch({ operationId: OperationId.UploadFile }));
                return new ErrOption(
                    `File ${uploadPath} already exists`,
                    UploadShareFileThunkError.FileExists,
                );
            }
        }

        const uploadRes = await webdavApi.copyOrCreateFile(
            stubUrl,
            user,
            userId,
            folder,
            uploadPath,
            content,
            () => {},
        );
        if (!uploadRes.success) {
            dispatch(
                stopFetch({
                    operationId: OperationId.UploadFile,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                }),
            );
            return new ErrOption(
                uploadRes.result.message,
                UploadShareFileThunkError.FileUploadFailed,
            );
        }

        const serverFiles =
            (await dispatch(appActions.getServerFiles(folder))) ?? [];
        const serverFile = serverFiles.find((item) => item.name === fileBase);

        if (!serverFile) {
            dispatch(
                stopFetch({
                    operationId: OperationId.UploadFile,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('load_serverfiles_error'),
                    status: NotificationStatus.Error,
                }),
            );
            return new ErrOption(
                `Uploaded file ${uploadPath} not found`,
                UploadShareFileThunkError.UploadedFileNotFound,
            );
        }

        batch(() => {
            dispatch(
                stopFetch({
                    operationId: OperationId.UploadFile,
                }),
            );
            dispatch(
                showNotification({
                    message: translate('upload_finished_header'),
                    status: NotificationStatus.Success,
                }),
            );
            dispatch(setFilesToUpload([serverFile]));
        });

        return serverFile;
    };
};

type UploadShareLargeFileData = Omit<UploadFileData, 'content'> & {
    allowOverwrite: boolean;
    file: File;
};

export const uploadShareLargeFileThunk = ({
    folder,
    file,
    fileBase,
    allowOverwrite,
}: UploadShareLargeFileData): ThunkAction<
    Promise<
        | ServerFile
        | ErrOption<UploadShareFileThunkError | WebdavApiErrorSubCode>
    >,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState) {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return new ErrOption(
                'Invalid state',
                UploadShareFileThunkError.InvalidState,
            );
        }

        const uploadPath = `${folder}/${fileBase}`;

        const isFolderCreated = await createShareFolders(folder);

        if (!isFolderCreated) {
            return new ErrOption(
                `Could not create folder for ${uploadPath}`,
                UploadShareFileThunkError.FolderCreationFailed,
            );
        }

        if (!allowOverwrite) {
            const fileExistsRes = await webdavApi.exists(
                stubUrl,
                user,
                userId,
                uploadPath,
            );

            if (!fileExistsRes.success) {
                return new ErrOption(
                    fileExistsRes.result.message,
                    UploadShareFileThunkError.FileExistsCheckFailed,
                );
            }
            if (fileExistsRes.result) {
                return new ErrOption(
                    `File ${uploadPath} already exists`,
                    UploadShareFileThunkError.FileExists,
                );
            }
        }

        const abortController = new AbortController();
        chunkedUploadAbortControllerRefBox.setValue(abortController);

        const retryOptions: RetryOptions = {
            maxRetries: MAX_UPLOAD_RETRIES,
            baseDelayMs: BASE_DELAY_MS,
            calculateDelay: (retryAttempt, baseDelayMs) => {
                return baseDelayMs * 2 ** (retryAttempt - 1);
            },
        };

        const chunkedFileParams: UploadChunkedFileParams = {
            destination: uploadPath,
            content: file.stream(),
            fileSize: file.size,
            signal: abortController.signal,
            chunkSizeBytes: state.appReducer.appConfig.chunkSizeMb * MEGABYTE,
            onUploadProgress: (progress) => {
                dispatch({
                    type: appConstants.APP_PROGRESS_SIZE,
                    payload: { progressSize: progress },
                });
            },
            retryOptions,
        };

        const uploadRes = await webdavApi.uploadChunkedFile({
            requestConfig: { stubUrl, user, userId },
            ...chunkedFileParams,
        });

        if (!uploadRes.success) {
            if (
                uploadRes.result.errorSubCode ===
                WebdavApiErrorSubCode.NetworkError
            ) {
                return new ErrOption(
                    uploadRes.result.message,
                    WebdavApiErrorSubCode.NetworkError,
                );
            }

            return new ErrOption(
                uploadRes.result.message,
                UploadShareFileThunkError.FileUploadFailed,
            );
        }

        const serverFiles =
            (await dispatch(appActions.getServerFiles(folder))) ?? [];
        const serverFile = serverFiles.find((item) => item.name === fileBase);

        if (!serverFile) {
            return new ErrOption(
                `Uploaded file ${uploadPath} not found`,
                UploadShareFileThunkError.UploadedFileNotFound,
            );
        }

        batch(() => {
            dispatch(
                showNotification({
                    message: translate('upload_finished_header'),
                    status: NotificationStatus.Success,
                }),
            );
            dispatch(setFilesToUpload([serverFile]));
        });

        return serverFile;
    };
};

export const createFolderThunk = ({
    path,
    folderName,
}: CreateFolderData): ThunkAction<void, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState) {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return;
        }

        dispatch(
            startFetch({
                id: OperationId.CreateFolder,
                message: translate('creating_item'),
            }),
        );

        const res = await webdavApi.createNewFolder(
            stubUrl,
            user,
            userId,
            path,
            folderName,
        );
        if (!res.success) {
            await dispatch(appActions.getServerFiles(path));
            dispatch(
                stopFetch({
                    operationId: OperationId.CreateFolder,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        await dispatch(appActions.getServerFiles(path));

        batch(() => {
            dispatch(
                stopFetch({
                    operationId: OperationId.CreateFolder,
                }),
            );
            dispatch(
                showNotification({
                    message: translate('item_created'),
                    status: NotificationStatus.Success,
                }),
            );
        });
    };
};

export const shareServerFileByEmailThunk = (
    email: string,
    file: ServerFile,
    sharePermissions: SharePermissions,
): ThunkAction<void, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState) {
        const state = getState();
        const stubUrl = selectStubUrl(state);
        const user = selectUser(state);
        const translate = selectTranslate(state);

        if (!stubUrl || !user) {
            logger.error(
                `Error getting stubUrl or user: stubUrl=${stubUrl}, user=${user}`,
                'Share',
            );
            return;
        }

        dispatch(
            startFetch({
                id: OperationId.EmailShare,
                message: translate('creating_share'),
            }),
        );

        const shareesRes = await nextcloudApi.searchSharees({
            requestConfig: {
                stubUrl,
                user,
            },
            searchTerm: email,
            itemType: ItemType.Folder,
            shareTypes: [ShareType.User],
        });

        if (!shareesRes.success) {
            dispatch(
                stopFetch({
                    operationId: OperationId.EmailShare,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        const sharee = shareesRes.result.ocs.data.exact.users[0];

        if (!sharee) {
            dispatch(
                stopFetch({
                    operationId: OperationId.EmailShare,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        const shareRes = await nextcloudApi.shareByPath({
            requestConfig: {
                stubUrl,
                user,
            },
            shareTargetPath: file.path,
            shareType: sharee.value.shareType,
            shareWith: sharee.value.shareWith,
            permissions: sharePermissions,
        });

        if (!shareRes.success) {
            dispatch(
                stopFetch({
                    operationId: OperationId.EmailShare,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        const url = `${user.server}/index.php/f/${file.fileId}`;

        dispatch(stopFetch({ operationId: OperationId.EmailShare }));
        submitDialogResult(
            new EmailShareDialogResult({
                name: file.name,
                url,
            }),
        );
    };
};

export const searchUserIdsThunk = (
    query: string,
): ThunkAction<
    Promise<ReturnType<typeof nextcloudApi.searchAutocomplete>>,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(
        dispatch,
        getState,
    ): Promise<ReturnType<typeof nextcloudApi.searchAutocomplete>> {
        const state = getState();
        const queryToUserIdsResponse = selectQueryToUserIdsResponse(state);

        const userIdsResponse = queryToUserIdsResponse[query];
        if (userIdsResponse) {
            return userIdsResponse;
        }

        const stubUrl = selectStubUrl(state);
        const user = selectUser(state);

        if (!stubUrl || !user) {
            logger.error(
                `Error getting stubUrl or user: stubUrl=${stubUrl}, user=${user}`,
                'Auth',
            );
            return {
                success: false,
                result: {
                    message: 'Error getting stubUrl or user',
                    errorCode: NextcloudAppErrorCode.nextcloudApi,
                    errorSubCode: undefined,
                },
            };
        }
        const accountIdsRes = await nextcloudApi.searchAutocomplete({
            requestConfig: {
                stubUrl,
                user,
            },
            query,
            shareTypes: [ShareType.User],
        });

        if (accountIdsRes.success) {
            dispatch(setUserIdsResponse(query, accountIdsRes));
        }
        return accountIdsRes;
    };
};

export const shareServerFileThunk = ({
    path,
    expirationDate,
    password,
}: {
    path: string;
    expirationDate?: Date | null;
    password?: string;
}): ThunkAction<
    Promise<OCSOkResponseBody<Share> | null>,
    RootState,
    unknown,
    AnyAction
> => {
    return async function thunk(dispatch, getState) {
        const state = getState();
        const stubUrl = selectStubUrl(state);
        const user = selectUser(state);
        const translate = selectTranslate(state);
        if (!stubUrl || !user) {
            logger.error(
                `Error getting stubUrl or user: stubUrl=${stubUrl}, user=${user}`,
                'Share',
            );
            return null;
        }

        dispatch(
            startFetch({
                id: OperationId.ServerFileShare,
                message: translate('creating_share'),
            }),
        );

        const expirationDateStr = Utilities.formatUploadDate(expirationDate);
        const shareRes = await nextcloudApi.shareByPath({
            requestConfig: {
                stubUrl,
                user,
            },
            shareTargetPath: path,
            shareType: ShareType.PublicLink,
            permissions: SharePermissions.Read,
            expirationDate: expirationDateStr,
            password,
        });

        if (!shareRes.success) {
            dispatch(
                stopFetch({
                    operationId: OperationId.ServerFileShare,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return null;
        }

        dispatch(
            stopFetch({
                operationId: OperationId.ServerFileShare,
            }),
        );

        const fileName = shareRes.result.ocs.data.path
            .split('/')
            .filter(Boolean)
            .pop();

        submitDialogResult(
            new ShareFileDialogResult({
                url: shareRes.result.ocs.data.url,
                name: fileName,
                password,
                expirationdate: expirationDateStr,
            }),
        );
        return shareRes.result;
    };
};

export const loadDndFilesThunk = (): ThunkAction<
    void,
    RootState,
    unknown,
    AnyAction
> => {
    return function thunk(dispatch, getState) {
        const state = getState();
        const translate = selectTranslate(state);
        const context = selectTeamsContext(getState());
        if (!context) {
            logger.error('No teams context found', 'Teams');
            return;
        }

        dispatch(
            startFetch({
                id: OperationId.LoadDndFiles,
                message: translate('prepare_accounts_fetch'),
            }),
        );

        const replyToId = selectReplyToId(state);
        const inputThreadId = context.channel?.id || context.chat?.id || '';
        const replyToIdForThreads = replyToId || '';
        const uniqueId = replyToIdForThreads + inputThreadId;

        microsoftTeams.thirdPartyCloudStorage.getDragAndDropFiles(
            uniqueId,
            async (files, error) => {
                if (error) {
                    logger.error(
                        `getDragAndDropFiles error: ${
                            error instanceof Error
                                ? error.message
                                : String(error)
                        }`,
                        'Files',
                    );
                    dispatch(
                        stopFetch({
                            operationId: OperationId.LoadDndFiles,
                            message: 'Error loading files',
                            errorCode: AppErrorCode.CustomError,
                            status: NotificationStatus.Error,
                        }),
                    );
                    return;
                }

                const fileContentArr = await Promise.all(
                    files.map(async (file) => file.arrayBuffer()),
                );

                batch(() => {
                    dispatch(
                        setFilesToUpload(
                            mapPlainFilesToFileItems(
                                files as File[],
                                fileContentArr,
                            ),
                        ),
                    );
                    dispatch(
                        stopFetch({
                            operationId: OperationId.LoadDndFiles,
                        }),
                    );
                });
            },
        );
    };
};

export const shareDndFilesThunk = ({
    files,
    folder,
    password,
    expirationDate,
    onUploadProgress = () => {},
}: {
    files: FileItem[];
    folder: string;
    password?: string;
    expirationDate?: Date | null;
    onUploadProgress?: (progress: number) => void;
}): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return;
        }

        const isFolderCreated = await createShareFolders(folder);

        if (!isFolderCreated) {
            dispatch(
                showNotification({
                    message: translate('could_not_create_folder'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
            return;
        }

        const requestConfig = {
            stubUrl,
            user,
            userId,
        } as const;

        const { uploadResponses, finalFilenames } = await uploadFilesInParallel(
            {
                files,
                folder,
                onUploadProgress,
                requestConfig,
            },
        );

        if (uploadResponses.some(isResponseError)) {
            logger.error(
                `uploadResults: ${JSON.stringify(uploadResponses)}`,
                'Upload',
            );

            dispatch(
                showNotification({
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (uploadResponses.every(isResponseError)) {
                return;
            }
        }

        const expirationDateStr = Utilities.formatUploadDate(expirationDate);
        const uploadedFilenames = finalFilenames.filter(
            (filename, idx) => uploadResponses[idx].success,
        );
        const shareResponses = await Promise.all(
            uploadedFilenames.map((filename) =>
                nextcloudApi.shareByPath({
                    requestConfig,
                    shareTargetPath: `${folder}/${filename}`,
                    shareType: ShareType.PublicLink,
                    permissions: SharePermissions.Read,
                    password,
                    expirationDate: expirationDateStr,
                }),
            ),
        );

        if (shareResponses.some(isResponseError)) {
            logger.error(
                `shareResponses: ${JSON.stringify(shareResponses)}`,
                'Share',
            );

            dispatch(
                showNotification({
                    message: translate('share_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (shareResponses.every(isResponseError)) {
                return;
            }
        }

        const sharedFileInfoArr = [];
        for (let i = 0; i < shareResponses.length; i++) {
            const shareRes = shareResponses[i];
            if (!shareRes.success) {
                continue;
            }

            sharedFileInfoArr.push({
                name: uploadedFilenames[i],
                url: shareRes.result.ocs.data.url,
            });
        }

        submitDialogResult(
            new DndPublicShareDialogResult({
                sharedFileInfoArr,
                password,
                expirationDate: expirationDateStr,
            }),
        );

        if (shareResponses.some((res) => !res.success)) {
            dispatch(
                showNotification({
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
        }
    };
};

export const shareDndFilesWithCircleThunk = ({
    files,
    folder,
    selectedCircle,
    password,
    expirationDate,
    onUploadProgress = () => {},
}: {
    files: FileItem[];
    folder: string;
    selectedCircle: Circle;
    password?: string;
    expirationDate?: Date | null;
    onUploadProgress?: (progress: number) => void;
}): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        const teamsContext = selectTeamsContext(state);
        const isChannelOrTeam = Boolean(
            teamsContext?.channel?.id || teamsContext?.team?.groupId,
        );
        if (!user || !userId || !stubUrl) {
            return;
        }

        const isFolderCreated = await createShareFolders(folder);

        if (!isFolderCreated) {
            dispatch(
                showNotification({
                    message: translate('could_not_create_folder'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
            return;
        }

        const requestConfig = {
            stubUrl,
            user,
            userId,
        } as const;

        const { uploadResponses, finalFilenames } = await uploadFilesInParallel(
            {
                files,
                folder,
                onUploadProgress,
                requestConfig,
            },
        );

        if (uploadResponses.some(isResponseError)) {
            logger.error(
                `uploadResults: ${JSON.stringify(uploadResponses)}`,
                'Upload',
            );

            dispatch(
                showNotification({
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (uploadResponses.every(isResponseError)) {
                return;
            }
        }

        const expirationDateStr = Utilities.formatUploadDate(expirationDate);
        const uploadedFilenames = finalFilenames.filter(
            (filename, idx) => uploadResponses[idx].success,
        );

        if (isChannelOrTeam && selectedCircle) {
            const circleShareResponses = await Promise.all(
                uploadedFilenames.map((filename) => {
                    return nextcloudApi.shareByPath({
                        requestConfig,
                        shareTargetPath: `${folder}/${filename}`,
                        shareType: ShareType.Circle,
                        shareWith: selectedCircle.id,
                        permissions: SharePermissions.Read,
                        password,
                        expirationDate: expirationDateStr,
                    });
                }),
            );

            if (circleShareResponses.every(isResponseError)) {
                return;
            }

            if (circleShareResponses.some(isResponseError)) {
                logger.error(
                    `shareResponses: ${JSON.stringify(circleShareResponses)}`,
                    'Share',
                );

                dispatch(
                    showNotification({
                        message: translate('share_selected_error'),
                        status: NotificationStatus.Error,
                        errorCode: AppErrorCode.CustomError,
                    }),
                );
            }

            const sharedFileInfoArr = [];
            for (let i = 0; i < circleShareResponses.length; i++) {
                const shareRes = circleShareResponses[i];
                if (!shareRes.success) {
                    continue;
                }

                sharedFileInfoArr.push({
                    name: uploadedFilenames[i],
                    url: `${user.server}/index.php/f/${shareRes.result.ocs.data.file_source}`,
                });
            }

            submitDialogResult(
                new DndShareWithCirclesDialogResult({
                    sharedFileInfoArr,
                }),
            );
            return;
        }
    };
};

export const emailShareDndFilesThunk = ({
    files,
    folder,
    email,
    permissions,
    onUploadProgress = () => {},
}: {
    files: FileItem[];
    folder: string;
    email: string;
    permissions: SharePermissions;
    onUploadProgress?: (progress: number) => void;
}): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
    return async function thunk(dispatch, getState): Promise<void> {
        const state = getState();
        const user = selectUser(state);
        const userId = selectUserId(state);
        const stubUrl = selectStubUrl(state);
        const translate = selectTranslate(state);
        if (!user || !userId || !stubUrl) {
            return;
        }

        const shareesRes = await nextcloudApi.searchSharees({
            requestConfig: {
                stubUrl,
                user,
            },
            searchTerm: email,
            itemType: ItemType.Folder,
            shareTypes: [ShareType.User],
        });

        if (!shareesRes.success) {
            dispatch(
                showNotification({
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        const sharee = shareesRes.result.ocs.data.exact.users[0];

        if (!sharee) {
            dispatch(
                stopFetch({
                    operationId: OperationId.EmailShare,
                    errorCode: AppErrorCode.CustomError,
                    message: translate('something_went_wrong'),
                    status: NotificationStatus.Error,
                }),
            );
            return;
        }

        const isFolderCreated = await createShareFolders(folder);

        if (!isFolderCreated) {
            dispatch(
                showNotification({
                    message: translate('could_not_create_folder'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
            return;
        }

        const requestConfig = {
            stubUrl,
            user,
            userId,
        } as const;

        const { uploadResponses, finalFilenames } = await uploadFilesInParallel(
            {
                files,
                folder,
                onUploadProgress,
                requestConfig,
            },
        );

        if (uploadResponses.some(isResponseError)) {
            logger.error(
                `uploadResults: ${JSON.stringify(uploadResponses)}`,
                'Upload',
            );
            dispatch(
                showNotification({
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (uploadResponses.every(isResponseError)) {
                return;
            }
        }

        const uploadedFilenames = finalFilenames.filter(
            (filename, idx) => uploadResponses[idx].success,
        );
        const shareResponses = await Promise.all(
            uploadedFilenames.map((filename) =>
                nextcloudApi.shareByPath({
                    shareTargetPath: `${folder}/${filename}`,
                    shareType: sharee.value.shareType,
                    shareWith: sharee.value.shareWith,
                    permissions: permissions,
                    requestConfig,
                }),
            ),
        );

        if (shareResponses.some(isResponseError)) {
            logger.error(
                `shareResponses: ${JSON.stringify(shareResponses)}`,
                'Share',
            );
            dispatch(
                showNotification({
                    message: translate('share_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );

            if (shareResponses.every(isResponseError)) {
                return;
            }
        }

        const sharedFileInfoArr: SharedFileInfo[] = [];
        for (let i = 0; i < shareResponses.length; i++) {
            const shareRes = shareResponses[i];
            if (!shareRes.success) {
                continue;
            }

            sharedFileInfoArr.push({
                name: uploadedFilenames[i],
                // eslint-disable-next-line max-len
                url: `${user.server}/index.php/f/${shareRes.result.ocs.data.file_source}`,
            });
        }

        submitDialogResult(
            new DndEmailShareDialogResult({
                sharedFileInfoArr,
            }),
        );

        if (shareResponses.some(isResponseError)) {
            dispatch(
                showNotification({
                    message: translate('upload_selected_error'),
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                }),
            );
        }
    };
};
